import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { BaseHttpService } from '@app/core/http';
import { JwtService } from '@app/core/auth';
import { environment } from '../../../environments/environment';
import {
  DEFAULT_ENABLED_MODULES,
  DEFAULT_UI_SETTINGS,
  EnabledModules,
  ModuleKey,
  PriceDisplayMode,
  UiSettings,
  isEnabledModules,
  isPriceDisplayMode,
} from '@app/models/ui-settings.model';

const CONFIG_ID = 'GESTION_PRODUITS_SETTINGS';

@Injectable({ providedIn: 'root' })
export class UiSettingsService extends BaseHttpService {
  private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;

  private readonly stateSubject = new BehaviorSubject<UiSettings>(DEFAULT_UI_SETTINGS);
  readonly state$: Observable<UiSettings> = this.stateSubject.asObservable();
  readonly priceDisplayMode$: Observable<PriceDisplayMode> = this.state$.pipe(
    map(s => s.priceDisplayMode),
    distinctUntilChanged(),
  );

  readonly enabledModules$: Observable<EnabledModules> = this.state$.pipe(
    map(s => s.enabledModules),
    distinctUntilChanged((a, b) => a.tables === b.tables && a.vehicles === b.vehicles && a.rooms === b.rooms),
  );

  // True once the first refresh (success or failure) has settled.
  // Guards and consumers that need the *real* server state must wait on this
  // before reading enabledModules — otherwise they read DEFAULT_UI_SETTINGS
  // (all true) and bypass the toggle on cold-load.
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);
  readonly loaded$: Observable<boolean> = this.loadedSubject.asObservable();

  private currentConfigOid: string | null = null;

  constructor(private jwtSvc: JwtService, http: HttpClient) {
    super(jwtSvc, http);
    this.jwtSvc.token$.subscribe(token => {
      if (token) {
        this.loadedSubject.next(false);
        this.refresh().subscribe({ error: () => {} });
      } else {
        this.stateSubject.next(DEFAULT_UI_SETTINGS);
        this.currentConfigOid = null;
        this.loadedSubject.next(false);
      }
    });
  }

  getMode(): PriceDisplayMode {
    return this.stateSubject.value.priceDisplayMode;
  }

  isModuleEnabled(key: ModuleKey): boolean {
    return this.stateSubject.value.enabledModules[key];
  }

  refresh(): Observable<UiSettings> {
    const orgId = this.getOrgId();
    if (!orgId) {
      this.stateSubject.next(DEFAULT_UI_SETTINGS);
      this.loadedSubject.next(true);
      return of(DEFAULT_UI_SETTINGS);
    }

    const body = {
      status: 'active',
      enterpriseId: orgId,
      options: { 'options.configId': CONFIG_ID },
      excluded: {},
    };

    return this.http
      .post<any>(
        `${this.baseApiSpeedPresta}${orgId}/${environment.configRetrieveUrl}`,
        body,
        { headers: this.getAuthHeaders() },
      )
      .pipe(
        map(raw => this.parseSettings(raw)),
        tap(({ settings, oid }) => {
          this.currentConfigOid = oid;
          this.stateSubject.next(settings);
          this.loadedSubject.next(true);
        }),
        map(({ settings }) => settings),
        catchError(err => {
          if (isDevMode()) console.warn('[UiSettingsService] refresh failed, using defaults', err);
          this.currentConfigOid = null;
          this.stateSubject.next(DEFAULT_UI_SETTINGS);
          this.loadedSubject.next(true);
          return of(DEFAULT_UI_SETTINGS);
        }),
      );
  }

  update(patch: Partial<UiSettings>): Observable<UiSettings> {
    const orgId = this.getOrgId();
    if (!orgId) return of(this.stateSubject.value);

    const next: UiSettings = { ...this.stateSubject.value, ...patch };

    const data = {
      configId: CONFIG_ID,
      priceDisplayMode: next.priceDisplayMode,
      enabledModules: next.enabledModules,
    };

    const request$ = this.currentConfigOid
      ? this.http.post<any>(
          `${this.baseApiSpeedPresta}${orgId}/${environment.configUpdateUrl}`,
          {
            enterpriseId: orgId,
            options: { keyValue: this.currentConfigOid, data },
            excluded: {},
          },
          { headers: this.getAuthHeaders() },
        )
      : this.http.post<any>(
          `${this.baseApiSpeedPresta}${orgId}/${environment.configCreateUrl}`,
          {
            status: 'active',
            enterpriseId: orgId,
            options: { appId: 'CONFIG', ...data },
            excluded: {},
          },
          { headers: this.getAuthHeaders() },
        );

    return request$.pipe(
      tap((res: any) => {
        if (!this.currentConfigOid) {
          this.currentConfigOid =
            res?._id?.$oid || res?._id || res?.data?._id?.$oid || res?.data?._id || null;
        }
        this.stateSubject.next(next);
      }),
      map(() => next),
    );
  }

  private getOrgId(): string | undefined {
    return this.getUserByToken()?.organization?.toUpperCase();
  }

  private parseSettings(raw: any): { settings: UiSettings; oid: string | null } {
    const doc = Array.isArray(raw) ? raw[0] : raw;
    const options = doc?.options ?? doc?.data ?? {};
    const mode = options.priceDisplayMode;
    const oid = doc?._id?.$oid || doc?._id || null;
    const rawModules = options.enabledModules;
    const enabledModules = isEnabledModules(rawModules) ? rawModules : DEFAULT_ENABLED_MODULES;

    return {
      settings: {
        priceDisplayMode: isPriceDisplayMode(mode)
          ? mode
          : DEFAULT_UI_SETTINGS.priceDisplayMode,
        enabledModules,
      },
      oid,
    };
  }
}
